import { RestError } from "@tastyworks/result"
import {
  DOCUMENT_SLUGS,
  SupportingDocumentVerification,
  SupportingDocumentVerificationStatus,
  TastyworksRest,
} from "@tastyworks/tastyworks-api"
import { pick } from "@tastyworks/ui-library"
import { difference } from "lodash-es"

export const SUPPORTING_DOCUMENT_SLUGS = pick(
  DOCUMENT_SLUGS,
  "ID_DOCUMENT",
  "ADDRESS_DOCUMENT",
  "SSN_DOCUMENT"
)

export const ENTITY_DOCUMENT_SLUGS = pick(
  DOCUMENT_SLUGS,
  "ENTITY_AGREEMENT",
  "PROOF_OF_ENTITY",
  "OFFERING_MEMORANDUM",
  "SUBSCRIPTION_AGREEMENT"
)

export const SUPPORTING_DOCUMENT_SLUG_VALUES = Object.values(
  SUPPORTING_DOCUMENT_SLUGS
)

export const ENTITY_DOCUMENT_SLUG_VALUES = Object.values(ENTITY_DOCUMENT_SLUGS)

export type SupportingDocumentSlug =
  (typeof SUPPORTING_DOCUMENT_SLUG_VALUES)[number]
export type EntityDocumentSlug = (typeof ENTITY_DOCUMENT_SLUG_VALUES)[number]
export type DocumentSlug = SupportingDocumentSlug | EntityDocumentSlug

export type SsnSlug = typeof SUPPORTING_DOCUMENT_SLUGS.SSN_DOCUMENT

export const FOREIGNER_DOCS: SupportingDocumentSlug[] = [
  SUPPORTING_DOCUMENT_SLUGS.ID_DOCUMENT,
  SUPPORTING_DOCUMENT_SLUGS.ADDRESS_DOCUMENT,
]

/**
 * get the jumio iframe url for the document verification
 *
 * @param twApiClient api client
 * @param slug the name of the document
 * @param citizenshipCountry customer's country of citizenship
 * @param addressDocumentType the type of address document
 * @returns jumio iframe url used for verification
 */
export async function fetchVerificationUrlPromise(
  twApiClient: TastyworksRest.Client,
  slug: string,
  citizenshipCountry: string,
  addressDocumentType?: string,
  includeAddress = false
) {
  if (!citizenshipCountry) {
    console.assert(false, "citizenshipCountry is required")
  }
  const response =
    await twApiClient.supportingDocumentVerificationService.create({
      slug,
      documentCountry: citizenshipCountry,
      documentType: addressDocumentType?.trim(),
      includeAddress,
    })

  if (response.isError) {
    // TODO: find more adequate replacement for `dispatch("error")`
    throw new Error(response.errorContainer.message)
  }

  return response
}

/**
 * get the required documents for KYC
 * @param twApiClient api client
 * @param customer the customer model
 * @returns required verification documents
 */
export async function getRequiredDocuments(
  twApiClient: TastyworksRest.Client,
  isForeign: boolean
) {
  // TODO: Error Handling
  const promises = await Promise.all([
    twApiClient.customerService.getRequiredIdentityDocuments(),
    twApiClient.customerService.getSupportingDocuments(),
  ])

  promises.forEach((p) => {
    if (p.isError) {
      throw RestError.fromResponse(p, {
        resource: "Document",
        operation: "read",
      })
    }
  })

  const requiredIdentityDocuments = promises[0]
  const supportingDocuments = promises[1]

  // requested supporting documents are uploaded from CAM /accounts/summary
  const availableSlugs = supportingDocuments.items
    .filter((x) => x.isAvailable)
    .map((x) => x.slug as SupportingDocumentSlug)

  const identitySlugs = requiredIdentityDocuments.items.map(
    (x) => x.slug as SupportingDocumentSlug
  )

  if (isForeign) {
    for (const doc of FOREIGNER_DOCS) {
      if (!identitySlugs.includes(doc)) {
        identitySlugs.push(doc)
      }
    }
  }

  return difference(identitySlugs, availableSlugs)
}

/**
 * create a pending supporting document verification
 * @param extTransactionNumber external transaction number
 */
export function createPendingSupportingDocumentVerification(
  extTransactionNumber: string
) {
  const supportingDocumentVerification = new SupportingDocumentVerification()
  supportingDocumentVerification.extTransactionNumber = extTransactionNumber
  supportingDocumentVerification.status =
    SupportingDocumentVerificationStatus.PENDING
  return supportingDocumentVerification
}

/**
 * a transformation of the File data type to a serializable object
 * with a Base64 string content in the form of a data URL
 */
export interface SerializableFile {
  name: string
  type: string
  size: number
  content: string
}

export const EMPTY_FILE = {
  name: "Empty",
  type: "",
  size: 0,
  content: "",
} as const satisfies SerializableFile

/** serialize the file to a Base64 string */
function serializeFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file.size === 0) return resolve("")

    const reader = new FileReader()

    // Read the file's contents as a Base64 string
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
  })
}

/**
 * transform a file object to a serializable object with metadata and content
 * @param file file object to serialize
 * @returns serializable file object with metadata and content as a data URL
 */
export async function convertFileToSerializable(
  file: File
): Promise<SerializableFile> {
  // Get the Base64 content of the file
  const content = await serializeFile(file)

  // Return a JSON object with file metadata and content
  return {
    name: file.name,
    type: file.type,
    size: file.size,
    content: content, // Base64 string
  }
}

export function createFileFromSerializable(
  serializable: SerializableFile
): File {
  const { name, type, content } = serializable
  // Remove the "data:<type>;base64," part from the string
  const base64Data = content.split(",")[1] ?? ""
  const binaryString = atob(base64Data)
  const len = binaryString.length
  const bytes = new Uint8Array(len)

  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  // Reconstruct the file using the Uint8Array and File constructor
  const blob = new Blob([bytes], { type })
  return new File([blob], name, { type })
}

export const ACCEPTED_FILE_TYPES = ".jpg,.jpeg,.png,.gif,.pdf"
export const PDF_BLACKLISTED_FILE_TYPES = ".jpg,.jpeg,.png,.gif"
export const PDF_BLACKLISTED_DOCUMENT_TYPES: string[] = [
  SUPPORTING_DOCUMENT_SLUGS.ID_DOCUMENT,
  SUPPORTING_DOCUMENT_SLUGS.SSN_DOCUMENT,
]

export function getAcceptedFileTypes(slug: string) {
  return PDF_BLACKLISTED_DOCUMENT_TYPES.includes(slug)
    ? PDF_BLACKLISTED_FILE_TYPES
    : ACCEPTED_FILE_TYPES
}

/** max file size of 12 mb */
export const DOCUMENT_UPLOAD_MAX_FILE_SIZE = 12

if (import.meta.vitest) {
  const { it, expect, describe, vi } = import.meta.vitest

  describe("fetchVerificationUrlPromise", () => {
    const twApiClient = {
      supportingDocumentVerificationService: { create: vi.fn() },
    } as unknown as TastyworksRest.Client

    it("fetches the verification URL", async () => {
      vi.mocked(
        twApiClient.supportingDocumentVerificationService.create
      ).mockResolvedValue({ isError: false } as any)

      const response = await fetchVerificationUrlPromise(
        twApiClient,
        "ADDRESS_DOCUMENT",
        "USA",
        "Bank Statement"
      )

      expect(response).toBeDefined()
      expect(
        twApiClient.supportingDocumentVerificationService.create
      ).toBeCalledWith({
        slug: "ADDRESS_DOCUMENT",
        documentCountry: "USA",
        documentType: "Bank Statement",
        includeAddress: false,
      })
    })

    // FIXME: svelte5: getting a weird "reading indexOf undefined" error here
    it.skip("throws an error if the response is an error", async () => {
      vi.mocked(
        twApiClient.supportingDocumentVerificationService.create
      ).mockResolvedValue({
        isError: true,
        errorContainer: { message: "error" },
      } as any)

      await expect(
        fetchVerificationUrlPromise(twApiClient, "ID_DOCUMENT", "USA")
      ).rejects.toThrowError()
    })
  })

  describe("createPendingSupportingDocumentVerification", () => {
    it("creates a pending supporting document verification", () => {
      const result = createPendingSupportingDocumentVerification("123")
      expect(result.extTransactionNumber).toEqual("123")
      expect(result.status).toEqual(
        SupportingDocumentVerificationStatus.PENDING
      )
    })
  })

  describe("convertFileToSerializable", () => {
    it("serializes a file object to a serializable object", async () => {
      const file = new File(["foo"], "bar.txt", { type: "text/plain" })
      const result = await convertFileToSerializable(file)

      expect(result).toEqual({
        name: "bar.txt",
        type: "text/plain",
        size: 3,
        content: expect.stringContaining("data:text/plain;base64"),
      })
    })

    it("serializes an empty file object to a serializable object", async () => {
      const file = new File([], "empty.txt", { type: "text/plain" })
      const result = await convertFileToSerializable(file)

      expect(result).toEqual({
        name: "empty.txt",
        type: "text/plain",
        size: 0,
        content: "",
      })
    })
  })

  describe("createFileFromSerializable", () => {
    it("deserializes a SerializableFile to a File", async () => {
      const serializable = {
        name: "bar.txt",
        type: "text/plain",
        size: 3,
        content: "data:text/plain;base64,Zm9v",
      }

      const result = createFileFromSerializable(serializable)

      expect(result).toBeInstanceOf(File)
      expect(result.name).toEqual("bar.txt")
      expect(result.type).toEqual("text/plain")
      expect(result.size).toEqual(3)
    })
  })

  describe("getAcceptedFileTypes", () => {
    it.each([
      [SUPPORTING_DOCUMENT_SLUGS.ID_DOCUMENT, PDF_BLACKLISTED_FILE_TYPES],
      [SUPPORTING_DOCUMENT_SLUGS.ADDRESS_DOCUMENT, ACCEPTED_FILE_TYPES],
      [SUPPORTING_DOCUMENT_SLUGS.SSN_DOCUMENT, PDF_BLACKLISTED_FILE_TYPES],
    ])(
      "returns the accepted file types for %s",
      (slug: string, expected: string) => {
        const result = getAcceptedFileTypes(slug)
        expect(result).toEqual(expected)
      }
    )
  })
}
